import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@framework-controls/core';
import { strings } from 'src/localization';
import * as css from './MenuVehicle.module.scss';
import classNames from 'classnames';
import { ApptVeh, VehicleRec, UpdateApptVehInp, VehicleStockPhoto, PhotoList, SpecData } from 'src/models';
import VehicleCard from 'src/components/VehicleCard/VehicleCard';
import SVPButton from 'src/components/SVPButtons/SVPButton';
import { IAppointmentState } from 'src/store/states';
import { THEME } from 'src/constants/ThemeNames';

export interface Iprops {
    openFlag: boolean;
    close: (e: any) => void;
    apptVeh: ApptVeh;
    updateApptVehicle: (inp: UpdateApptVehInp) => void;
    getPhoto?: (year: string, make: string, model: string, id: string) => void;
    vehiclePhoto?: VehicleStockPhoto;
    vehPhotoList?: PhotoList;
    // bordered wrapper for the button's selected border - used to align the topline
    vehicleButtonRef?: React.RefObject<HTMLDivElement>;
    theme: string;
    showVehRef: React.RefObject<HTMLDivElement>
    newVehicleMileage: string,
    prevMileage: number,
    Itype: string | undefined;
    appointment: IAppointmentState;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const MenuVehicle = (props: Iprops) => {
    // Refs are always null in react test renderer, so in order to properly test we need to set a default ref value
    const initialValue: HTMLDivElement = document.createElement('div');
    const menuVehicleRef = useRef<HTMLDivElement>(initialValue);
    const containerRef = useRef<HTMLDivElement>(null);
    const toplineAreaRef = useRef<HTMLDivElement>(null);
    const [toplineWidth, setToplineWidth] = useState<number>();
    const [showToplineRight, setShowToplineRight] = useState(false);
    const vehicle: VehicleRec = useMemo(() => ({
        id: '',
        vin: props.apptVeh.vin,
        makeCode: props.apptVeh.makeCode,
        make: props.apptVeh.make,
        model: props.apptVeh.model,
        imagePath: props.apptVeh.imagePath,
        year: props.apptVeh.vehYr,
        color: props.apptVeh.vehColor,
        style: props.apptVeh.series,
        prevMileage: props.apptVeh.mileage,
        prevRoDate: '',
        nextAppt: '',
        lastAdv: '',
        lastAdvName: '',
        lastAdvImage: '',
        lastTrans: '',
        lastTransLit: '',
        nameId: '',
    }), [
        props.apptVeh.imagePath,
        props.apptVeh.make,
        props.apptVeh.mileage,
        props.apptVeh.model,
        props.apptVeh.series,
        props.apptVeh.vehColor,
        props.apptVeh.vehYr,
        props.apptVeh.vin,
        props.apptVeh.makeCode,
    ]);

    const changePrevMileage = useCallback((_: string, mileage: number) => {
        let convtMileage = mileage;
        if (isNaN(convtMileage)) {
            convtMileage = 0;
        }

        const inp: UpdateApptVehInp = {
            vin: vehicle.vin,
            makeCode: vehicle.makeCode,
            make: vehicle.make,
            model: vehicle.model,
            series: vehicle.style,
            vehYr: vehicle.year,
            vehColor: vehicle.color,
            mileage: convtMileage,
            imagePath: vehicle.imagePath,
        };
        props.updateApptVehicle(inp);
    }, [vehicle, props.updateApptVehicle]);

    const handleClickOutside = useCallback((e: any) => {
        if (!menuVehicleRef.current?.contains(e.target as Element) && !props.showVehRef.current?.contains(e.target as Element)) {
            props.close(e);
        }
    }, [menuVehicleRef, props.close, props.showVehRef]);

    useEffect(() => {
        if (props.openFlag) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [props.openFlag, handleClickOutside]);

    const menuContentClasses = classNames(
        css.menuContent,
        {
            [css.menuMini]: props.theme === 'mini',
            [css.menuBuick]: props.theme === 'buick',
            [css.menuMB]: props.theme === 'mercedesBenz',
            [css.menuContentAudi]: props.theme === THEME.AUDI,

        },
    );

    /** Measures the gap between the topline and the button's visible border so the join stays flush. */
    useLayoutEffect(function measureToplineOnOpenAndResize() {
        if (!props.openFlag) {
            return;
        }
        // the click target is narrower than the bordered wrapper, so measure against the wrapper instead
        const measureTopline = () => {
            const buttonEl = props.vehicleButtonRef?.current;
            const buttonRect = buttonEl?.getBoundingClientRect();
            if (!toplineAreaRef.current || !containerRef.current || !buttonEl || !buttonRect?.width) {
                return;
            }
            // Under 600px the border is dropped and the top line is hidden. Vehicle's visible border is drawn by
            // .container, not .menuContent, so read the border from there - .menuContent's never paints.
            if (!parseFloat(getComputedStyle(containerRef.current).borderLeftWidth)) {
                setToplineWidth(undefined);

                return;
            }
            const toplineAreaLeft = toplineAreaRef.current.getBoundingClientRect().left;
            const buttonStyle = getComputedStyle(buttonEl);
            // target is always the button's left border edge (2px normally, 1px Buick, 0px mobile);
            // toplineRight is just rotated 180deg, so it still reaches the same edge, growing left instead
            const targetX = buttonRect.left + (parseFloat(buttonStyle.borderLeftWidth) || 0);
            const gap = targetX - toplineAreaLeft;
            setShowToplineRight(gap < 0);
            setToplineWidth(Math.round(Math.abs(gap) * 100) / 100);
        };
        // the flyout may still be laying out on the very next frame, so wait a second one:
        // frame 1 lets layout settle, frame 2 reads the settled DOM
        const scheduleMeasurement = () => {
            let secondFrame: number | undefined = undefined;
            const firstFrame = requestAnimationFrame(() => {
                secondFrame = requestAnimationFrame(measureTopline);
            });

            return () => {
                cancelAnimationFrame(firstFrame);
                if (secondFrame !== undefined) {
                    cancelAnimationFrame(secondFrame);
                }
            };
        };

        let cancelPendingMeasurement = scheduleMeasurement();
        const handleResize = () => {
            // cancel any pending two-frame chain so rapid resizes don't stack measurements
            cancelPendingMeasurement();
            cancelPendingMeasurement = scheduleMeasurement();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            cancelPendingMeasurement();
            window.removeEventListener('resize', handleResize);
        };
    }, [props.openFlag, props.vehicleButtonRef]);

    const closeLitClass = classNames(
        css.closeLit,
        {
            [css.closeLitBMW]: props.theme === 'bmw',
            [css.closeLitFord]: props.theme === 'ford',
            [css.closeLitHonda]: props.theme === 'honda',
            [css.closeLitIN]: props.theme === 'infiniti',
            [css.closeLitMB]: props.theme === 'mercedesBenz',
            [css.closeLitAcura]: props.theme === 'acura',
        },
    );

    if (!props.openFlag) {
        return null;
    }

    const closeBtn = props.theme === THEME.AUDI ?
        <SVPButton
            id='cancelBtn'
            variant={'secondaryBtnAlt'}
            message={strings.Close}
            handler={props.close}
            theme={props.theme}
            noArrow={true}
        /> :
        <Button
            variant={'text'}
            id='cancelBtn'
            onClick={props.close}>
            <div className={closeLitClass}>{strings.Close}</div>
        </Button>;

    return (
        <div id='VehicleFlyoutContent' className={menuContentClasses} ref={menuVehicleRef} data-testid='refdiv'>
            <div className={css.container} ref={containerRef} data-testid='container-div'>
                <div className={css.toplineArea} ref={toplineAreaRef} data-testid='toplineArea-div'>
                    {showToplineRight ?
                        (<div className={css.toplineRight} data-testid='toplineRight-div'
                            style={toplineWidth === undefined ? undefined : { width: `${toplineWidth}px` }}>
                        </div>) :
                        (<div className={css.toplineLeft} data-testid='toplineLeft-div'
                            style={toplineWidth === undefined ? undefined : { width: `${toplineWidth}px` }}>
                        </div>)}
                </div>
                <div className={css.VehDiv}>
                    <VehicleCard
                        vehicle={vehicle}
                        updateMileageState={changePrevMileage}
                        menu={true}
                        theme={props.theme}
                        newVehicleMileage={props.newVehicleMileage}
                        prevMileage={props.prevMileage}
                        apptMileage={0}
                        cancellingAppointment={false}
                        Itype={props.Itype}
                        specData={props.specData}
                        sessionId = {props.sessionId}
                        lexusMCLaunched = {props.lexusMCLaunched}
                        lexusTDMCLaunched = {props.lexusTDMCLaunched}
                        toyotaMCLaunched = {props.toyotaMCLaunched}
                        toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                        appointment = {props.appointment}
                    />
                </div>
                <div className={css.btnArea}>
                    <div className={classNames(css.closeBtnWrap,
                        { [css.closeBtnWrapAudi]: props.theme === THEME.AUDI })}>
                        {closeBtn}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default MenuVehicle;